'use client';

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Card, CardContent, Typography, IconButton, Box, Modal, Menu, MenuItem, Button } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Scatter,
  ScatterChart,
  CartesianGrid,
  Cell,
  Rectangle,
} from 'recharts';
import eyeIcon from 'packages/novous_web/src/assets/icons/Eye.svg';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';
import OpenInFullIcon from 'packages/novous_web/src/assets/icons/Expand.svg';
import EditIcon from 'packages/novous_web/src/assets/icons/edit.svg';
import RestartAltIcon from 'packages/novous_web/src/assets/icons/Reset.svg';
import CollapseIcon from 'packages/novous_web/src/assets/icons/CollapseIcon.svg';
import NumericalAnalysisTable from '../SafetyAnalytics/NumericalAnalysisTable';
import CommonAnalysisTable from './CommonAnalysisTable';
import EditDataModal from './EditDataModal';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';
import ArrowLeftIcon from 'packages/novous_web/src/assets/icons/ArrowLeft.svg';
import ArrowRightIcon from 'packages/novous_web/src/assets/icons/next.svg';

interface DayWiseIdealTime {
  vehicleId: string;
  warnings: number;
  date?: string;
}

interface SpeedData {
  record_date: string;
  average_speed: number;
  lane_departure_alerts: number;
}

interface DayNightDrivingData {
  record_date: string;
  day_driving_hours: number;
  night_driving_hours: number;
  total_driving_hours: number;
}

interface BarChartCommonProps {
  dayWiseIdealTimeResponses?: DayWiseIdealTime[];
  speedDataResponses?: SpeedData[];
  dayNightDrivingData?: DayNightDrivingData[];
  HeaderName?: string;
  showFullName?: boolean;
  sideTitle?: string;
  DownTitle?: string;
  chartType?: 'warnings' | 'speed' | 'day-night-hours';
  infoIcon?: React.ReactNode;
  onCardDataUpdate?: (chartId: number, startDate: string, endDate: string) => void;
  chartId?: number;
  tableColumns?: any[];
  tableData?: any[];
  tableTitle?: string;
  tableHeaderText?: string;
  paginationInfo?: {
    currentPage?: number;
    pageSize?: number;
    totalRecords?: number;
    hasMorePages?: boolean;
    nextCursor?: any;
    previousCursor?: any;
  };
  onTableIconClick?: () => void;
  onPaginationChange?: (cursor?: any, direction?: 'next' | 'previous') => void;
  onTablePaginationChange?: (newPage: number) => void;
  tooltipLabel?: string;
  useFleetTableStyle?: boolean;
  paginatedData?: any;
}

// Custom bar shape for split bar (night on top - yellow, day on bottom - purple)
const SplitBarShape = (props: any) => {
  const { x, y, width, height, dayHours = 0, nightHours = 0, totalHours = 0 } = props;
  const radius = 4;

  if (!dayHours || !nightHours || totalHours === 0) {
    // Fallback to single color bar if data is missing
    return (
      <path
        d={`
          M ${x},${y + height}
          L ${x},${y + radius}
          Q ${x},${y} ${x + radius},${y}
          L ${x + width - radius},${y}
          Q ${x + width},${y} ${x + width},${y + radius}
          L ${x + width},${y + height}
          Z
        `}
        fill="#8884d8"
      />
    );
  }

  // Calculate proportions for day and night
  const nightHeight = (nightHours / totalHours) * height; // Night on top
  const dayHeight = (dayHours / totalHours) * height; // Day on bottom

  return (
    <g>
      {/* Night part (yellow) - TOP HALF with rounded corners */}
      <path
        d={`
          M ${x},${y + radius}
          Q ${x},${y} ${x + radius},${y}
          L ${x + width - radius},${y}
          Q ${x + width},${y} ${x + width},${y + radius}
          L ${x + width},${y + nightHeight}
          L ${x},${y + nightHeight}
          Z
        `}
        fill="#F2C94C"
      />

      {/* Day part (purple) - BOTTOM HALF */}
      <path
        d={`
          M ${x},${y + nightHeight}
          L ${x},${y + height}
          L ${x + width},${y + height}
          L ${x + width},${y + nightHeight}
          Z
        `}
        fill="#3a9a49"
      />
    </g>
  );
};

// Simple custom bar shape for other chart types
const SimpleBarShape = (props: any) => {
  const { x, y, width, height, fill, payload } = props;
  const radius = 4;

  // Don't render the bar if the value is 0 or undefined
  if (!payload || payload.value === 0 || payload.value === undefined) {
    return null;
  }

  return (
    <path
      d={`
        M ${x},${y + height}
        L ${x},${y + radius}
        Q ${x},${y} ${x + radius},${y}
        L ${x + width - radius},${y}
        Q ${x + width},${y} ${x + width},${y + radius}
        L ${x + width},${y + height}
        L ${x},${y + height}
        Z
      `}
      fill={fill}
    />
  );
};

// Rounded bar shape for expanded view
const RoundedBarShape = (props: any) => {
  const { x, y, width, height, fill, payload } = props;
  // Use smaller radius proportional to bar width, max 8px
  const radius = Math.min(8, width / 2, height / 2);

  // Don't render the bar if the value is 0 or undefined
  if (!payload || payload.value === 0 || payload.value === undefined) {
    return null;
  }

  return (
    <path
      d={`
        M ${x},${y + height}
        L ${x},${y + radius}
        Q ${x},${y} ${x + radius},${y}
        L ${x + width - radius},${y}
        Q ${x + width},${y} ${x + width},${y + radius}
        L ${x + width},${y + height}
        L ${x},${y + height}
        Z
      `}
      fill={fill}
    />
  );
};

const BarChartCommon: React.FC<BarChartCommonProps> = ({
  dayWiseIdealTimeResponses,
  speedDataResponses,
  dayNightDrivingData,
  HeaderName,
  showFullName,
  sideTitle,
  DownTitle,
  chartType = 'warnings',
  infoIcon,
  onCardDataUpdate,
  chartId,
  tableColumns,
  tableData,
  tableTitle,
  tableHeaderText,
  paginationInfo,
  onTableIconClick,
  onPaginationChange,
  onTablePaginationChange,
  tooltipLabel = 'Warnings',
  useFleetTableStyle = false,
  paginatedData,
}) => {
  const theme = useTheme();
  const { showToast } = useToast();
  const windowStart = useRef(0);
  const [chartData, setChartData] = useState<any[]>([]);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  const [isExpandedModalOpen, setIsExpandedModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [resetTrigger, setResetTrigger] = useState<number>(0);
  const open = Boolean(anchorEl);

  // Memoize the data sources based on whether paginatedData is provided
  const effectiveData = useMemo(() => {
    console.log('📦 effectiveData useMemo called:');
    console.log('   paginatedData:', paginatedData);
    console.log('   chartType:', chartType);
    console.log('   dayWiseIdealTimeResponses:', dayWiseIdealTimeResponses);

    if (paginatedData?.data) {
      console.log('✅ Using paginatedData.data:', paginatedData.data);
      return paginatedData.data;
    }

    switch (chartType) {
      case 'speed':
        console.log('✅ Using speedDataResponses:', speedDataResponses);
        return speedDataResponses || [];
      case 'day-night-hours':
        console.log('✅ Using dayNightDrivingData:', dayNightDrivingData);
        return dayNightDrivingData || [];
      default:
        console.log('✅ Using dayWiseIdealTimeResponses:', dayWiseIdealTimeResponses);
        return dayWiseIdealTimeResponses || [];
    }
  }, [paginatedData, chartType, speedDataResponses, dayNightDrivingData, dayWiseIdealTimeResponses]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    if (paginationInfo?.totalRecords && paginationInfo?.pageSize) {
      return Math.ceil(paginationInfo.totalRecords / paginationInfo.pageSize);
    }
    return 1;
  }, [paginationInfo?.totalRecords, paginationInfo?.pageSize]);

  // Helper booleans to detect presence of next/previous cursors across possible shapes
  const hasNextCursor = Boolean(paginatedData?.next_cursor ?? paginationInfo?.nextCursor);
  const hasPreviousCursor = Boolean(paginatedData?.previous_cursor ?? paginationInfo?.previousCursor);

  // Get current page
  // const currentPage = useMemo(() => {
  //   return paginationInfo?.currentPage || 1;
  // }, [paginationInfo?.currentPage]);

  // Generate page numbers to display (max 3 pages)
  // const visiblePages = useMemo(() => {
  //   if (totalPages <= 3) {
  //     return Array.from({ length: totalPages }, (_, i) => i + 1);
  //   }

  //   if (currentPage === 1) {
  //     return [1, 2, 3];
  //   }

  //   if (currentPage === totalPages) {
  //     return [totalPages - 2, totalPages - 1, totalPages];
  //   }

  //   return [currentPage - 1, currentPage, currentPage + 1];
  // }, [currentPage, totalPages]);

  // Transform data for chart display
  const transformChartData = useCallback(
    (data: any[]) => {
      if (!data || data.length === 0) {
        console.log('⚠️ transformChartData: No data provided');
        return [];
      }
      console.log('🔄 transformChartData received:', data);
      console.log('📊 Chart type:', chartType);
      return data.map((item) => {
        // Handle paginatedData structure
        if (item.vehicle_id !== undefined || item.vehicleId !== undefined) {
          // This is a vehicle/warning item
          const vehicleId = item.vehicle_id || item.vehicleId || '';
          // Support multiple field names for warnings / utilization data (include compliance and productivity fields)
          const warnings = Number(
            item.total_compliance_warnings ??
              item.warnings ??
              item.statistical_productivity_score ??
              item.utilization_percentage ??
              item.utilisation_percentage ??
              item.seat_belt_violations ??
              item.compliance_score ??
              item.value ??
              0,
          );

          console.log(`📍 Vehicle ${vehicleId}: warnings = ${warnings}, raw item:`, item);

          return {
            name: showFullName ? vehicleId : `${vehicleId.substring(0, 3)}...${vehicleId.slice(-3)}`,
            originalName: vehicleId,
            value: warnings,
          };
        } else if (item.record_date !== undefined) {
          // This is a date-based item (speed or day-night hours)
          const date = new Date(item.record_date);
          const formattedDate = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          });

          if (chartType === 'speed') {
            return {
              name: formattedDate,
              originalName: item.record_date,
              value: item.lane_departure_alerts || 0,
              lane_departure_alerts: item.lane_departure_alerts || 0,
              average_speed: item.average_speed || 0,
            };
          } else if (chartType === 'day-night-hours') {
            return {
              name: formattedDate,
              originalName: item.record_date,
              value: item.total_driving_hours || 0,
              dayHours: item.day_driving_hours || 0,
              nightHours: item.night_driving_hours || 0,
              totalHours: item.total_driving_hours || 0,
              dayPercentage:
                item.total_driving_hours > 0
                  ? ((item.day_driving_hours || 0) / item.total_driving_hours) * 100
                  : 0,
              nightPercentage:
                item.total_driving_hours > 0
                  ? ((item.night_driving_hours || 0) / item.total_driving_hours) * 100
                  : 0,
            };
          }
        }

        // Fallback for other structures
        return {
          name: item.name || '',
          originalName: item.originalName || '',
          value: item.value || 0,
        };
      });
    },
    [chartType, showFullName],
  );

  // Update chart data when effectiveData changes
  useEffect(() => {
    console.log('🔄 useEffect: effectiveData changed:', effectiveData);
    console.log('   Length:', effectiveData.length);
    if (effectiveData.length > 0) {
      const transformedData = transformChartData(effectiveData);
      console.log('✅ Transformed data:', transformedData);
      setChartData(transformedData);

      // For window scrolling (only for non-paginated data)
      if (!paginatedData) {
        windowStart.current = effectiveData.length > 100 ? effectiveData.length - 100 : 0;
        setHasPrevious(windowStart.current > 0);
        setHasNext(windowStart.current + 100 < effectiveData.length);
      }
    } else {
      console.log('❌ No effectiveData available');
      setChartData([]);
      if (!paginatedData) {
        setHasPrevious(false);
        setHasNext(false);
      }
    }
  }, [effectiveData, transformChartData, paginatedData]);

  // Handle window scrolling for non-paginated data
  const handlePrevious = () => {
    if (hasPrevious && !paginatedData) {
      windowStart.current = Math.max(0, windowStart.current - 100);
      const windowData = effectiveData.slice(windowStart.current, windowStart.current + 100);
      setChartData(transformChartData(windowData));
      setHasPrevious(windowStart.current > 0);
      setHasNext(windowStart.current + 100 < effectiveData.length);
    }
  };

  const handleNext = () => {
    if (hasNext && !paginatedData) {
      windowStart.current = Math.min(effectiveData.length - 100, windowStart.current + 100);
      const windowData = effectiveData.slice(windowStart.current, windowStart.current + 100);
      setChartData(transformChartData(windowData));
      setHasPrevious(windowStart.current > 0);
      setHasNext(windowStart.current + 100 < effectiveData.length);
    }
  };

  const handelTable = () => {
    setIsTableOpen(!isTableOpen);
    // Call the onTableIconClick callback if it exists to populate table data
    if (onTableIconClick && !isTableOpen) {
      onTableIconClick();
    }
  };

  const getXAxisConfig = () => {
    if (chartType === 'speed' || chartType === 'day-night-hours') {
      return {
        tick: { fontSize: 12 },
      };
    } else {
      return {
        tick: false,
      };
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          {chartType === 'speed' ? (
            <>
              <p
                style={{
                  fontWeight: 'bold',
                  margin: '0 0 8px 0',
                  color: theme.palette.text.primary,
                }}
              >
                {`Date: ${payload[0].payload.originalName}`}
              </p>
              <p
                style={{
                  margin: '4px 0',
                  color: theme.palette.text.primary,
                }}
              >
                {`Lane Departure Alerts: ${payload[0].payload.lane_departure_alerts}`}
              </p>
              <p
                style={{
                  margin: '4px 0',
                  color: theme.palette.text.primary,
                }}
              >
                {`Average GPS Speed: ${payload[0].payload.average_speed?.toFixed(2) || '0'} km/h`}
              </p>
            </>
          ) : chartType === 'day-night-hours' ? (
            <>
              <p
                style={{
                  fontWeight: 'bold',
                  margin: '0 0 8px 0',
                  color: theme.palette.text.primary,
                }}
              >
                {`Date: ${payload[0].payload.originalName}`}
              </p>
              <p
                style={{
                  margin: '4px 0',
                  color: theme.palette.text.primary,
                }}
              >
                {`Total Driving Hours: ${payload[0].payload.totalHours?.toFixed(2) || '0'}`}
              </p>
              <p
                style={{
                  margin: '4px 0',
                  color: theme.palette.text.primary,
                }}
              >
                {`Day Hours: ${payload[0].payload.dayHours?.toFixed(2) || '0'} (${
                  payload[0].payload.dayPercentage?.toFixed(1) || '0'
                }%)`}
              </p>
              <p
                style={{
                  margin: '4px 0',
                  color: '#F2C94C',
                }}
              >
                {`Night Hours: ${payload[0].payload.nightHours?.toFixed(2) || '0'} (${
                  payload[0].payload.nightPercentage?.toFixed(1) || '0'
                }%)`}
              </p>
            </>
          ) : (
            <>
              <p
                style={{
                  fontWeight: 'bold',
                  margin: '0 0 8px 0',
                  color: theme.palette.text.primary,
                }}
              >
                {`Vehicle: ${payload[0].payload.originalName || label}`}
              </p>
              <p
                style={{
                  margin: '4px 0',
                  color: theme.palette.text.primary,
                }}
              >
                {`${tooltipLabel}: ${payload[0].value}${
                  tooltipLabel === 'Score' || tooltipLabel === 'Utilization' ? '%' : ''
                }`}
              </p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpand = () => {
    console.log('Expand clicked for:', HeaderName);
    setIsExpandedModalOpen(true);
    handleMenuClose();
  };

  const handleEditData = () => {
    console.log('Edit Data clicked for:', HeaderName);
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleApplyEditData = (data: any) => {
    console.log('Applied edit data:', data);
    if (onCardDataUpdate && chartId) {
      onCardDataUpdate(chartId, data.startDate, data.endDate);
      const startDateFormatted = new Date(data.startDate).toLocaleDateString('en-GB');
      const endDateFormatted = new Date(data.endDate).toLocaleDateString('en-GB');
      showToast(`Date range applied: ${startDateFormatted} to ${endDateFormatted}`, 'success');
    }
  };

  const handleResetChanges = () => {
    console.log('Reset Changes clicked for:', HeaderName);
    handleMenuClose();
    setIsEditModalOpen(false);
    setResetTrigger((prev) => prev + 1);
    if (onCardDataUpdate && chartId) {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      onCardDataUpdate(chartId, startDate, endDate);
    }
  };

  const handleCloseExpandedModal = () => {
    setIsExpandedModalOpen(false);
  };

  // Add these state variables at the top of the BarChartCommon component
  const [cursorHistory, setCursorHistory] = useState<any[]>([]); // Store cursor history
  const [currentCursorIndex, setCurrentCursorIndex] = useState<number>(-1); // Track current position in history
  const [pageHistory, setPageHistory] = useState<{ cursor: any; pageNum: number }[]>([]); // Map cursors to page numbers
  // Local override to immediately reflect page changes in UI when navigating via cursors
  const [currentPageState, setCurrentPageState] = useState<number | null>(null);
  // Stack of next_cursors we explicitly sent when user clicked Next. We'll pop these when clicking Previous to return.
  const [sentNextCursors, setSentNextCursors] = useState<any[]>([]);

  // Compute current page, prefer server-provided `paginationInfo.currentPage`, then local override,
  // then derive from cursor index (index 0 => page 2)
  const currentPage = useMemo(() => {
    // Prefer the local override first so UI updates immediately when user navigates.
    if (currentPageState !== null) {
      return currentPageState;
    }

    if (paginationInfo?.currentPage !== undefined && paginationInfo?.currentPage !== null) {
      return paginationInfo.currentPage;
    }

    if (currentCursorIndex >= 0) {
      return currentCursorIndex + 2;
    }

    return 1;
  }, [currentPageState, paginationInfo?.currentPage, currentCursorIndex]);

  // Effect to store server-provided next cursor for the subsequent page (do NOT assume navigation occurred)
  useEffect(() => {
    // Only handle next_cursor here to store the token for page (current server page + 1)
    if (paginatedData?.next_cursor) {
      const serverCurrentPage = paginationInfo?.currentPage ?? 1;
      const nextPage = serverCurrentPage + 1;

      console.log('🔄 Storing server next_cursor for page:', {
        next_cursor: paginatedData.next_cursor,
        nextPage,
      });

      // If we don't already have an entry for that page, add to pageHistory
      const exists = pageHistory.some(
        (p) =>
          p.pageNum === nextPage && JSON.stringify(p.cursor) === JSON.stringify(paginatedData.next_cursor),
      );
      if (!exists) {
        setPageHistory((prev) => {
          // Keep earlier entries and append this page mapping
          const filtered = prev.filter((p) => p.pageNum !== nextPage);
          return [...filtered, { cursor: paginatedData.next_cursor, pageNum: nextPage }];
        });

        console.log('✅ Stored next cursor for page:', nextPage, paginatedData.next_cursor);
      }
    }
  }, [paginatedData?.next_cursor, paginationInfo?.currentPage, pageHistory]);

  // Debug: observe current page and history for troubleshooting
  useEffect(() => {
    console.log('📄 currentPage update:', {
      currentPage,
      currentPageState,
      currentCursorIndex,
      cursorHistoryLength: cursorHistory.length,
      hasNextCursor,
      hasPreviousCursor,
    });
  }, [
    currentPage,
    currentPageState,
    currentCursorIndex,
    cursorHistory.length,
    hasNextCursor,
    hasPreviousCursor,
  ]);

  // Debug: watch the stack of next cursors we sent
  useEffect(() => {
    console.log('🧾 sentNextCursors change:', sentNextCursors);
  }, [sentNextCursors]);

  // Clear the local page override when the server's currentPage matches it
  useEffect(() => {
    if (
      currentPageState !== null &&
      paginationInfo?.currentPage !== undefined &&
      paginationInfo.currentPage === currentPageState
    ) {
      setCurrentPageState(null);
    }
  }, [paginationInfo?.currentPage, currentPageState]);

  // Update handleNextPage to track history and record the cursor we sent
  const handleNextPage = () => {
    const nextCursorAvail = paginatedData?.next_cursor ?? paginationInfo?.nextCursor;
    console.log('🔼 handleNextPage called:', {
      nextCursorAvail,
      currentCursorIndex,
      cursorHistoryLength: cursorHistory.length,
    });

    if (nextCursorAvail && onPaginationChange) {
      console.log('✅ Calling onPaginationChange with next cursor:', nextCursorAvail);

      // Remember the cursor we are sending so Previous can use it to return
      setSentNextCursors((prev) => {
        const newSent = [...prev, nextCursorAvail];
        console.log('🔺 sentNextCursors updated:', newSent);
        return newSent;
      });

      // Add to cursor history / update index
      const newIndex = currentCursorIndex + 1;
      if (cursorHistory.length <= newIndex) {
        // This is a new forward navigation
        const newHistory = [...cursorHistory, nextCursorAvail];
        setCursorHistory(newHistory);
        setCurrentCursorIndex(newIndex);

        // Update page history (index 0 => page 2)
        const newPageHistory = [
          ...pageHistory,
          {
            cursor: nextCursorAvail,
            pageNum: newIndex + 2,
          },
        ];
        setPageHistory(newPageHistory);
      } else {
        // We already have this in history, just move the index
        setCurrentCursorIndex(newIndex);
      }

      // Immediately reflect page number in UI by incrementing
      setCurrentPageState((prev) => (prev !== null ? prev + 1 : (paginationInfo?.currentPage ?? 1) + 1));

      onPaginationChange(nextCursorAvail, 'next');
    } else {
      console.log('❌ Cannot go to next page:', {
        reason: !nextCursorAvail ? 'No next cursor' : 'No onPaginationChange callback',
      });
    }
  };

  // Update handlePreviousPage to prefer the last next_cursor we explicitly sent
  const handlePreviousPage = () => {
    const prevCursorAvail = paginatedData?.previous_cursor ?? paginationInfo?.previousCursor;

    console.log('🔽 handlePreviousPage called:', {
      currentCursorIndex,
      cursorHistoryLength: cursorHistory.length,
      prevCursorAvail,
      sentNextCursorsLength: sentNextCursors.length,
    });

    if (!onPaginationChange) {
      console.log('❌ Cannot go to previous page - no onPaginationChange callback');
      return;
    }

    // If we have a stack of next cursors we sent, pop it and send the previous cursor (or null for first page)
    if (sentNextCursors.length > 0) {
      const prevLen = sentNextCursors.length;
      const newStack = sentNextCursors.slice(0, -1);
      const cursorToSend = newStack.length > 0 ? newStack[newStack.length - 1] : null;

      console.log('✅ Popping sentNextCursors:', {
        stackBefore: prevLen,
        stackAfter: newStack.length,
        cursorToSend,
      });

      // update the stack
      setSentNextCursors(newStack);

      // reflect page immediately (decrement)
      setCurrentPageState((prev) =>
        prev !== null ? Math.max(1, prev - 1) : Math.max(1, (paginationInfo?.currentPage ?? 2) - 1),
      );

      // Send the cursor that corresponds to the page we want to load (null = first page)
      if (cursorToSend === null) {
        console.log('➡️ Returning to first page — sending null cursor');
        onPaginationChange(null, 'previous');
      } else {
        console.log('➡️ Returning to previous page — sending cursor:', cursorToSend);
        onPaginationChange(cursorToSend, 'previous');
      }

      return;
    }

    // If we're beyond the first cursor, go back one step in history
    if (currentCursorIndex > 0) {
      const previousIndex = currentCursorIndex - 1;
      const previousCursor = cursorHistory[previousIndex];
      console.log('✅ Going back to cursor at index:', previousIndex, 'cursor:', previousCursor);
      setCurrentCursorIndex(previousIndex);
      // reflect page immediately
      setCurrentPageState(previousIndex + 2);
      onPaginationChange(previousCursor, 'previous');
      return;
    }

    // If we're at index 0 (we are on page 2), go back to first page — send null cursor to request page 1
    if (currentCursorIndex === 0) {
      const lastNextCursor = cursorHistory[0];
      console.log(
        '✅ Going back to first page. Previously stored cursor for page2:',
        lastNextCursor,
        '— sending null to load page1',
      );
      setCurrentCursorIndex(-1);
      // reflect page 1 immediately
      setCurrentPageState(1);
      onPaginationChange(null, 'previous');
      return;
    }

    // Fallback: use API provided previous cursor if available
    if (prevCursorAvail) {
      console.log('✅ Calling onPaginationChange with previous cursor from API:', prevCursorAvail);
      onPaginationChange(prevCursorAvail, 'previous');
      // reflect page change if possible
      setCurrentPageState(Math.max(1, currentPage - 1 || 1));
      return;
    }

    console.log('❌ Cannot go to previous page - no history and no previous cursor from API or no callback');
  };

  // Update handlePageClick
  const handlePageClick = (pageNum: number) => {
    console.log(`📄 Clicked page ${pageNum}`, {
      currentPage,
      pageHistory: pageHistory.map((p) => p.pageNum),
    });

    if (pageNum === currentPage || !onPaginationChange) {
      console.log('Already on this page or no callback');
      return;
    }

    // If user clicked the first page, always request first page with null cursor
    if (pageNum === 1) {
      console.log('➡️ Jump to first page requested — sending null cursor');
      setCurrentCursorIndex(-1);
      setCurrentPageState(1);
      setSentNextCursors([]);
      onPaginationChange(null, pageNum > currentPage ? 'next' : 'previous');
      return;
    }

    // Find cursor for this page in history
    const pageInfo = pageHistory.find((p) => p.pageNum === pageNum);

    if (pageInfo) {
      // We have this page in history
      const historyIndex = pageHistory.findIndex((p) => p.pageNum === pageNum);
      console.log('📚 Found page in history at index:', historyIndex, 'cursor:', pageInfo.cursor);

      setCurrentCursorIndex(historyIndex);
      // Immediately update displayed page
      setCurrentPageState(pageNum);
      onPaginationChange(pageInfo.cursor, pageNum > currentPage ? 'next' : 'previous');
    } else {
      // We don't have this page in history
      console.log('❌ Page not in history, cannot jump directly');

      // For now, we can only navigate sequentially
      if (pageNum > currentPage) {
        // Try to go forward from current cursor
        const nextCursorAvail =
          paginatedData?.next_cursor ?? paginationInfo?.nextCursor ?? paginationInfo?.nextCursor;
        if (nextCursorAvail) {
          console.log('➡️ Going forward from current cursor');
          // reflect the expected page immediately
          setCurrentPageState(currentPage + 1);
          onPaginationChange(nextCursorAvail, 'next');
        }
      } else if (pageNum < currentPage && currentCursorIndex > 0) {
        // Try to go back in history
        const targetIndex = currentCursorIndex - 1;
        if (cursorHistory[targetIndex]) {
          console.log('⬅️ Going back in history to index:', targetIndex);
          setCurrentCursorIndex(targetIndex);
          // reflect the expected page immediately
          setCurrentPageState(pageNum);
          onPaginationChange(cursorHistory[targetIndex], 'previous');
        }
      }
    }
  };

  // Update the visiblePages calculation to show actual page numbers
  const visiblePages = useMemo(() => {
    // Use total pages from paginationInfo or calculate from history
    const actualTotalPages = totalPages > 0 ? totalPages : cursorHistory.length + 1;

    if (actualTotalPages <= 3) {
      return Array.from({ length: actualTotalPages }, (_, i) => i + 1);
    }

    if (currentPage === 1) {
      return [1, 2, 3];
    }

    if (currentPage === actualTotalPages) {
      return [actualTotalPages - 2, actualTotalPages - 1, actualTotalPages];
    }

    return [currentPage - 1, currentPage, currentPage + 1];
  }, [currentPage, totalPages, cursorHistory.length]);

  return (
    <>
      <Box className="w-full h-full flex flex-col border rounded-md p-4 items-start bg-white" sx={{ gap: 1 }}>
        <Box className="w-full flex justify-between items-center">
          <Box className="flex items-center" sx={{ gap: 1 }}>
            <Typography
              variant="subtitle2"
              sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600 })}
            >
              {HeaderName}
            </Typography>
            {infoIcon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                }}
              >
                {infoIcon}
              </Box>
            )}
          </Box>
          <Box className="flex items-center" sx={{ gap: 1 }}>
            <Box
              className="flex items-center cursor-pointer"
              onClick={handelTable}
              sx={{ pt: '10px', gap: 0.3 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                }}
              >
                <img src={eyeIcon} alt="Table" width={24} height={24} style={{ display: 'block' }} />
              </Box>
              <Typography
                variant="body2"
                fontSize={14}
                sx={(theme) => ({ color: theme.palette.primary.main })}
              >
                Table
              </Typography>
            </Box>
            <Box className="flex items-center gap-1 cursor-pointer" sx={{ pt: '10px' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                }}
              >
                <IconButton
                  sx={{
                    p: 0,
                    width: '24px !important',
                    height: '24px !important',
                    minWidth: '24px !important',
                    minHeight: '24px !important',
                    maxWidth: '24px !important',
                    maxHeight: '24px !important',
                    flexShrink: 0,
                    display: 'flex !important',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                  onClick={handleMenuClick}
                >
                  <img
                    src={menuIcon}
                    alt="Menu"
                    width="24"
                    height="24"
                    style={{
                      display: 'block',
                      width: '24px',
                      height: '24px',
                      minWidth: '24px',
                      minHeight: '24px',
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box className="rounded-xl border-[#F0EFF8] w-full">
          <div className="flex" style={{ gap: '8px' }}>
            {chartData && chartData.length > 0 && (
              <Typography
                variant="caption"
                color="textPrimary"
                sx={{
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                  textAlign: 'center',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '18px',
                  letterSpacing: '0%',
                  color: '#1B1B1B',
                }}
              >
                {sideTitle ||
                  (chartType === 'speed'
                    ? 'Lane Departure Alerts'
                    : chartType === 'day-night-hours'
                    ? 'Driving Hours'
                    : 'Number of Warnings')}
              </Typography>
            )}
            <div className="flex-1 min-h-[200px] max-h-[400px] relative">
              {chartType === 'speed' && chartData && chartData.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: '-3px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 10,
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: '#F2C94C',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '21px',
                      letterSpacing: '0%',
                      color: '#1B1B1B',
                    }}
                  >
                    Average GPS Speed
                  </Typography>
                </Box>
              )}
              {chartType === 'day-night-hours' && chartData && chartData.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 2,
                    zIndex: 10,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '25px',
                        backgroundColor: '#F2C94C',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '21px',
                        letterSpacing: '0%',
                        color: '#1B1B1B',
                      }}
                    >
                      Hours Travelled in Night
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '25px',
                        backgroundColor: theme.palette.primary.main,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '21px',
                        letterSpacing: '0%',
                        color: '#1B1B1B',
                      }}
                    >
                      Hours Travelled in Day
                    </Typography>
                  </Box>
                </Box>
              )}
              {chartData && chartData.length > 0 ? (
                <ResponsiveContainer
                  width="100%"
                  height={300}
                  style={chartType === 'speed' ? { marginTop: '18px' } : undefined}
                >
                  {chartType === 'speed' ? (
                    <ComposedChart
                      data={chartData}
                      barGap={0}
                      barCategoryGap={15}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid stroke="#E5E5E5" vertical={false} />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                      <YAxis
                        domain={[0, 'auto']}
                        allowDataOverflow={false}
                        tick={{ fontSize: 14 }}
                        axisLine={false}
                        tickLine={false}
                        width={25}
                        tickFormatter={(value) => {
                          if (value === 0) return '0';
                          if (value >= 1000) {
                            return (value / 1000).toFixed(1) + 'k';
                          }
                          return Math.round(value).toString();
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                      <Bar
                        dataKey="value"
                        fill={theme.palette.primary.main}
                        shape={<SimpleBarShape />}
                        barSize={8}
                        activeBar={false}
                      />
                      <Scatter
                        dataKey="average_speed"
                        fill="#F2C94C"
                        name="Average GPS Speed"
                        shape={
                          <circle
                            r={4}
                            style={{
                              opacity: 1,
                            }}
                          />
                        }
                      />
                    </ComposedChart>
                  ) : chartType === 'day-night-hours' ? (
                    <BarChart
                      data={chartData}
                      barGap={0}
                      barCategoryGap={15}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid stroke="#E5E5E5" vertical={false} />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, dy: 10 }}
                      />
                      <YAxis
                        domain={[0, 'auto']}
                        allowDataOverflow={false}
                        tick={{ fontSize: 14 }}
                        axisLine={false}
                        tickLine={false}
                        width={35}
                        tickFormatter={(value) => {
                          if (value === 0) return '0';
                          if (value >= 1000) {
                            return (value / 1000).toFixed(1) + 'k';
                          }
                          return Math.round(value).toString();
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                      <Bar
                        dataKey="value"
                        shape={(props: any) => (
                          <SplitBarShape
                            {...props}
                            dayHours={props.payload.dayHours}
                            nightHours={props.payload.nightHours}
                            totalHours={props.payload.totalHours}
                          />
                        )}
                        barSize={8}
                        activeBar={false}
                      />
                    </BarChart>
                  ) : (
                    <BarChart
                      data={chartData}
                      barGap={0}
                      barCategoryGap={5}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid stroke="#E5E5E5" vertical={false} />
                      <XAxis dataKey="name" axisLine={false} tick={false} padding={{ left: 30, right: 30 }} />
                      <YAxis
                        domain={[0, 'auto']}
                        allowDataOverflow={false}
                        tick={{ fontSize: 14 }}
                        axisLine={false}
                        tickLine={false}
                        width={45}
                        tickFormatter={(value) => {
                          if (value === 0) return '0';
                          if (value >= 1000) {
                            return (value / 1000).toFixed(1) + 'k';
                          }
                          return Math.round(value).toString();
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                      <Bar
                        dataKey="value"
                        fill={theme.palette.primary.main}
                        shape={<SimpleBarShape />}
                        barSize={8}
                        activeBar={false}
                      />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px',
                    width: '100%',
                  }}
                >
                  <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
                </Box>
              )}
            </div>
          </div>

          {chartData && chartData.length > 0 && (
            <div className="w-full flex items-center justify-center gap-4 mt-2">
              <Typography
                sx={{
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '18px',
                  letterSpacing: '0%',
                  color: '#1B1B1B',
                }}
              >
                {chartType === 'day-night-hours' || chartType === 'speed' ? 'Date' : 'Vehicle ID'}
              </Typography>
            </div>
          )}
        </Box>

        {/* Pagination Controls */}
        {paginatedData && (
          <Box display="flex" justifyContent="center" alignItems="center" width={'100%'} gap={1} mt={2}>
            {/* Previous Button - disabled if we're on page 1 */}
            <IconButton
              size="small"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              sx={{
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                borderRadius: '8px',
                width: '32px',
                height: '32px',
                '&:disabled': {
                  opacity: 0.5,
                  borderColor: '#ccc',
                  cursor: 'not-allowed',
                },
              }}
            >
              <img src={ArrowLeftIcon} alt="Previous" style={{ width: '16px', height: '16px' }} />
            </IconButton>

            {/* Page Numbers (compact: show current and current+1 like NumericalAnalysisTable) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Current page - active */}
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  backgroundColor: theme.palette.primary.main,
                  color: '#FFFFFF',
                  fontSize: '12px',
                  fontWeight: 400,
                }}
              >
                {currentPage}
              </Box>

              {/* Next page (light) - show if there is a next page available */}
              {(hasNextCursor || currentPage < totalPages) && (
                <Box
                  onClick={() => handlePageClick(currentPage + 1)}
                  sx={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    color: '#808080',
                    fontSize: '12px',
                    fontWeight: 400,
                    '&:hover': {
                      backgroundColor: '#F5F5F5',
                    },
                  }}
                >
                  {currentPage + 1}
                </Box>
              )}

              {/* If there are more pages beyond next, show ellipsis + last page */}
              {totalPages > currentPage + 1 && (
                <>
                  <Typography sx={{ color: '#808080', fontSize: '12px' }}>...</Typography>
                  <Box
                    onClick={() => handlePageClick(totalPages)}
                    sx={{
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: totalPages === currentPage ? '#FFFFFF' : '#808080',
                      backgroundColor:
                        totalPages === currentPage ? theme.palette.primary.main : 'transparent',
                      fontSize: '12px',
                      fontWeight: 400,
                      '&:hover': {
                        backgroundColor: totalPages === currentPage ? theme.palette.primary.main : '#F5F5F5',
                      },
                    }}
                  >
                    {totalPages}
                  </Box>
                </>
              )}
            </Box>

            {/* Next Button - disabled if no next cursor AND we're on the last page */}
            <IconButton
              size="small"
              onClick={handleNextPage}
              disabled={!hasNextCursor && currentPage === totalPages}
              sx={{
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                borderRadius: '8px',
                width: '32px',
                height: '32px',
                '&:disabled': {
                  opacity: 0.5,
                  borderColor: '#ccc',
                  cursor: 'not-allowed',
                },
              }}
            >
              <img src={ArrowRightIcon} alt="Next" style={{ width: '16px', height: '16px' }} />
            </IconButton>
          </Box>
        )}
      </Box>
      <Modal
        open={isTableOpen}
        onClose={() => setIsTableOpen(false)}
        aria-labelledby="alert-table-modal"
        aria-describedby="alert-table-description"
      >
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            ...(useFleetTableStyle
              ? {
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  width: 'auto',
                  height: '90vh',
                  borderRadius: '8px',
                }
              : {
                  width: '90%',
                  height: '90%',
                  borderRadius: 2,
                }),
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {tableColumns && tableData ? (
            <CommonAnalysisTable
              title={tableTitle || 'Table View'}
              headerText={tableHeaderText || ''}
              columns={tableColumns}
              data={tableData}
              noDataMessage="No data available"
              onClose={() => setIsTableOpen(false)}
              paginationInfo={paginationInfo}
              onPaginationChange={onTablePaginationChange || onPaginationChange}
            />
          ) : (
            <NumericalAnalysisTable />
          )}
        </Box>
      </Modal>
      {/* Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
          },
        }}
      >
        <MenuItem onClick={handleExpand} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={OpenInFullIcon} alt="Expand" style={{ width: 24, height: 24 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Expand</Typography>
        </MenuItem>
        <MenuItem onClick={handleEditData} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Edit Data</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleResetChanges}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
        >
          <img
            src={RestartAltIcon}
            alt="Reset"
            style={{
              width: 20,
              height: 20,
              filter:
                'invert(34%) sepia(68%) saturate(2449%) hue-rotate(338deg) brightness(91%) contrast(84%)',
            }}
          />
          <Typography sx={{ fontSize: '14px', color: '#DC3545' }}>Reset Changes</Typography>
        </MenuItem>
      </Menu>

      {/* Expanded Modal */}
      <Modal open={isExpandedModalOpen} onClose={handleCloseExpandedModal}>
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '65%',
            height: '80%',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                {HeaderName}
              </Typography>
              {infoIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16 }}>{infoIcon}</Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                onClick={handleEditData}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={EditIcon} alt="Edit" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: theme.palette.primary.main,
                  }}
                >
                  Edit Chart
                </Typography>
              </Box>
              <Box
                onClick={handleCloseExpandedModal}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={CollapseIcon} alt="Collapse" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: theme.palette.primary.main,
                  }}
                >
                  Collapse
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Chart Content - Flex grow to fill space */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', minHeight: 0, gap: 2 }}>
            {/* Y-Axis Label */}
            {chartData && chartData.length > 0 && (
              <Typography
                variant="caption"
                color="textPrimary"
                sx={{
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#1B1B1B',
                }}
              >
                {sideTitle ||
                  (chartType === 'speed'
                    ? 'Lane Departure Alerts'
                    : chartType === 'day-night-hours'
                    ? 'Driving Hours'
                    : 'Number of Warnings')}
              </Typography>
            )}

            {/* Chart Container */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {chartData && chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  {chartType === 'speed' && speedDataResponses ? (
                    <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20 }}>
                      <CartesianGrid stroke="#E5E5E5" vertical={false} />
                      <XAxis
                        dataKey="originalName"
                        tick={{ fill: '#888', fontSize: 12 }}
                        axisLine={{ stroke: '#E5E5E5' }}
                        tickLine={false}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis
                        domain={[0, 'auto']}
                        tick={{ fill: '#888', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => {
                          if (value === 0) return '0';
                          if (value >= 1000) {
                            return (value / 1000).toFixed(1) + 'k';
                          }
                          return Math.round(value).toString();
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                      <Bar
                        dataKey="lane_departure_alerts"
                        fill={theme.palette.primary.main}
                        shape={<RoundedBarShape />}
                      />
                      <Scatter dataKey="average_speed" fill="#F2C94C" name="Average GPS Speed" />
                    </ComposedChart>
                  ) : chartType === 'day-night-hours' ? (
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20 }}>
                      <CartesianGrid stroke="#E5E5E5" vertical={false} />
                      <XAxis
                        dataKey="originalName"
                        tick={{ fill: '#888', fontSize: 12 }}
                        axisLine={{ stroke: '#E5E5E5' }}
                        tickLine={false}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis
                        domain={[0, 'auto']}
                        tick={{ fill: '#888', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => {
                          if (value === 0) return '0';
                          if (value >= 1000) {
                            return (value / 1000).toFixed(1) + 'k';
                          }
                          return Math.round(value).toString();
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                      <Bar
                        dataKey="value"
                        shape={(props: any) => (
                          <SplitBarShape
                            {...props}
                            dayHours={props.payload.dayHours}
                            nightHours={props.payload.nightHours}
                            totalHours={props.payload.totalHours}
                          />
                        )}
                      />
                    </BarChart>
                  ) : (
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20 }}>
                      <CartesianGrid stroke="#E5E5E5" vertical={false} />
                      <XAxis
                        dataKey="originalName"
                        tick={{ fill: '#888', fontSize: 12 }}
                        axisLine={{ stroke: '#E5E5E5' }}
                        tickLine={false}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis
                        domain={[0, 'auto']}
                        tick={{ fill: '#888', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => {
                          if (value === 0) return '0';
                          if (value >= 1000) {
                            return (value / 1000).toFixed(1) + 'k';
                          }
                          return Math.round(value).toString();
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                      <Bar dataKey="value" fill={theme.palette.primary.main} shape={<RoundedBarShape />} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    minHeight: '300px',
                    width: '100%',
                  }}
                >
                  <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
                </Box>
              )}
            </Box>
          </Box>

          {/* X-Axis Label */}
          {chartData && chartData.length > 0 && (
            <Box
              sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#1B1B1B',
                }}
              >
                {chartType === 'day-night-hours' || chartType === 'speed'
                  ? 'Date'
                  : DownTitle || 'Vehicle ID'}
              </Typography>
            </Box>
          )}

          {/* ADD PAGINATION FOR EXPANDED MODAL */}
          {paginatedData && chartData && chartData.length > 0 && (
            <Box display="flex" justifyContent="center" alignItems="center" width={'100%'} gap={1} mt={3}>
              {/* Previous Button */}
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                sx={{
                  border: '1px solid',
                  borderColor: theme.palette.primary.main,
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  '&:disabled': {
                    opacity: 0.5,
                    borderColor: '#ccc',
                    cursor: 'not-allowed',
                  },
                }}
              >
                <img src={ArrowLeftIcon} alt="Previous" style={{ width: '16px', height: '16px' }} />
              </IconButton>

              {/* Page Numbers */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Current page - active */}
                <Box
                  sx={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    backgroundColor: theme.palette.primary.main,
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: 400,
                  }}
                >
                  {currentPage}
                </Box>

                {/* Next page (light) */}
                {(hasNextCursor || currentPage < totalPages) && (
                  <Box
                    onClick={() => handlePageClick(currentPage + 1)}
                    sx={{
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'transparent',
                      color: '#808080',
                      fontSize: '12px',
                      fontWeight: 400,
                      '&:hover': {
                        backgroundColor: '#F5F5F5',
                      },
                    }}
                  >
                    {currentPage + 1}
                  </Box>
                )}

                {/* Ellipsis + Last page */}
                {totalPages > currentPage + 1 && (
                  <>
                    <Typography sx={{ color: '#808080', fontSize: '12px' }}>...</Typography>
                    <Box
                      onClick={() => handlePageClick(totalPages)}
                      sx={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: totalPages === currentPage ? '#FFFFFF' : '#808080',
                        backgroundColor:
                          totalPages === currentPage ? theme.palette.primary.main : 'transparent',
                        fontSize: '12px',
                        fontWeight: 400,
                        '&:hover': {
                          backgroundColor:
                            totalPages === currentPage ? theme.palette.primary.main : '#F5F5F5',
                        },
                      }}
                    >
                      {totalPages}
                    </Box>
                  </>
                )}
              </Box>

              {/* Next Button */}
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={!hasNextCursor && currentPage === totalPages}
                sx={{
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  '&:disabled': {
                    opacity: 0.5,
                    borderColor: '#ccc',
                    cursor: 'not-allowed',
                  },
                }}
              >
                <img src={ArrowRightIcon} alt="Next" style={{ width: '16px', height: '16px' }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Modal>
      {/* Edit Data Modal */}
      <EditDataModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onApply={handleApplyEditData}
        cardTitle={HeaderName}
        chartId={chartId}
        resetTrigger={resetTrigger}
      />
    </>
  );
};

export default BarChartCommon;
